/* 24K Drop — scroll story + grid reveal.
   Timeline over the pinned stage (t = 0..1), same on every screen size (the artifact's choreography):
     0–.20   fold slides up over the full-screen hero (hero recedes and dims)
     .20–.34 title is written in
     .33–.83 five brass cards are dealt in from alternate sides and land in the fan
     .80–1   copy rises in line by line
   Without the fold title (the hero art carries the headline) the title step is dropped and the stage is
   shorter (4.5 vh instead of 4.8); every other step keeps the artifact's scroll distance.
   Performance rules: one requestAnimationFrame loop; the scroll position is read once per frame and eased
   (so motion glides instead of stepping with each scroll tick); geometry is measured only on setup/resize;
   per frame we only write transform / opacity / filter, and only when the value changed.
   Listens to scroll in the capture phase so it also works when <body> is the scroller. */
(() => {
  if (window.K24Drop) return;

  const clamp = (v, a = 0, b = 1) => Math.min(b, Math.max(a, v));
  const ease = (x) => (x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2);
  const easeOut = (x) => 1 - Math.pow(1 - x, 3);
  const easeOutBack = (x) => { const c1 = 1.25, c3 = c1 + 1; return 1 + c3 * Math.pow(x - 1, 3) + c1 * Math.pow(x - 1, 2); };
  const mqPhone = matchMedia('(max-width: 767px)');
  const mqDesk = matchMedia('(min-width: 1024px)');
  const mqReduce = matchMedia('(prefers-reduced-motion: reduce)');
  const SMOOTH_MS = 110; // time constant of the scroll easing
  // phase timings (fractions of the pinned scroll); NOTITLE keeps each step's artifact scroll length on a 3.5 vh stage
  const T_TITLE = { fold: 0.2, cards: 0.33, cardStep: 0.075, cardDur: 0.2, copy: 0.8, lineStep: 0.035, lineDur: 0.1 };
  const T_NOTITLE = { fold: 0.217, cards: 0.26, cardStep: 0.081, cardDur: 0.217, copy: 0.771, lineStep: 0.038, lineDur: 0.109 };

  // write a style property only when it changes
  const put = (el, prop, val) => {
    if (!el) return;
    const c = el.__k24 || (el.__k24 = {});
    if (c[prop] === val) return;
    c[prop] = val;
    el.style[prop] = val;
  };

  class K24 {
    constructor(root) {
      this.root = root;
      const q = (s) => root.querySelector(s);
      const qa = (s) => Array.from(root.querySelectorAll(s));
      this.el = {
        stage: q('[data-k24-stage]'), sticky: q('[data-k24-sticky]'), hero: q('[data-k24-hero]'),
        shade: q('[data-k24-shade]'), fshadow: q('[data-k24-fshadow]'), fold: q('[data-k24-fold]'), cue: q('[data-k24-cue]'),
        title: q('[data-k24-title]'), wipe: q('[data-k24-wipe]'), wipeIn: q('[data-k24-wipe-in]'),
        deck: q('[data-k24-deck]'), cards: qa('[data-k24-card]'), lines: qa('[data-k24-line]'),
        video: q('[data-k24-video]'), tiles: qa('[data-k24-tile]'),
      };
      this.story = root.dataset.k24Story !== 'false';
      this.covered = false;
      this.cur = 0;
      this.raf = 0;

      this.tick = this.tick.bind(this);
      this.onScroll = () => { if (this.mode && !this.away && !this.raf) { this.last = performance.now(); this.raf = requestAnimationFrame(this.tick); } };
      // Android fires resize all the time while the URL bar slides; ignore those (same width, small height change).
      this.refresh = () => { this.setup(); this.update(); };
      this.onResize = () => {
        if (window.innerWidth === this.vw && Math.abs(window.innerHeight - this.vh) <= 150) return;
        this.refresh();
      };
      document.addEventListener('scroll', this.onScroll, { capture: true, passive: true });
      window.addEventListener('resize', this.onResize);
      [mqPhone, mqDesk, mqReduce].forEach((m) => m.addEventListener('change', this.refresh));

      // Pause everything (loop, shimmer, cue) while the story is off screen.
      if (typeof IntersectionObserver !== 'undefined' && this.el.stage) {
        this.stageIO = new IntersectionObserver(([e]) => {
          this.away = !e.isIntersecting;
          root.classList.toggle('is-away', this.away);
          if (!this.away) this.update();
        });
        this.stageIO.observe(this.el.stage);
      }

      this.setup();
      this.update();
      this.initVideo();
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(this.refresh);
      window.addEventListener('load', this.refresh, { once: true });
    }

    destroy() {
      document.removeEventListener('scroll', this.onScroll, { capture: true });
      window.removeEventListener('resize', this.onResize);
      [mqPhone, mqDesk, mqReduce].forEach((m) => m.removeEventListener('change', this.refresh));
      [this.io, this.nearIO, this.stageIO].forEach((o) => o && o.disconnect());
      if (this.raf) cancelAnimationFrame(this.raf);
    }

    setup() {
      const r = this.root, E = this.el;
      const vw = window.innerWidth, vh = window.innerHeight;
      // Phones resize on every URL-bar show/hide; only re-measure for real changes so the page does not jump.
      if (this.vw !== vw || !this.vh || Math.abs(vh - this.vh) > 150) {
        this.vw = vw; this.vh = vh;
        r.style.setProperty('--k24-vh', vh + 'px');
      }

      // Below 1024px the theme header is a floating bar; sit the film just under it.
      if (!mqDesk.matches) {
        const bar = document.querySelector('#main-header .menu-header-default');
        const b = bar && bar.getBoundingClientRect();
        if (b && b.height && b.bottom > 30 && b.bottom < 160) r.style.setProperty('--k24-hdr', Math.round(b.bottom + 8) + 'px');
      } else {
        r.style.removeProperty('--k24-hdr');
      }

      this.phone = mqPhone.matches;
      const motion = !mqReduce.matches;
      const on = this.story && motion && this.vh > 320 && this.vh < 1500;
      if (on !== this.mode) { this.mode = on; this.pinJS = false; this.pinChecked = false; }
      r.classList.toggle('is-scroll', on);
      r.classList.toggle('is-motion', motion);

      // Geometry used every frame — measured here only (transforms do not change layout).
      if (on && E.stage && E.fold) {
        const g = (this.geo = {});
        g.D = Math.max(1, E.stage.offsetHeight - this.vh);
        g.H = E.sticky.offsetHeight;
        g.W = E.fold.offsetWidth;
        g.wipe = E.wipe ? E.wipe.offsetWidth : 0;
        const dx0 = E.deck.offsetLeft, dy0 = E.deck.offsetTop;
        g.cards = E.cards.map((c) => {
          const cw = c.offsetWidth, ch = c.offsetHeight;
          return { cw, rx: dx0 + c.offsetLeft + cw / 2, ry: dy0 + c.offsetTop + ch / 2 };
        });
      }

      this.setupTiles(motion);
    }

    setupTiles(motion) {
      const tiles = this.el.tiles;
      if (!tiles.length) return;
      const gw = tiles[0].parentElement.offsetWidth || 1;
      tiles.forEach((t) => {
        const col = Math.round((t.offsetLeft / gw) * (this.phone ? 2 : 3));
        const d = (Math.min(col, 2) * 0.08).toFixed(2) + 's';
        if (t.__k24d !== d) { t.__k24d = d; t.style.setProperty('--k24-d', d); }
      });

      if (!motion || typeof IntersectionObserver === 'undefined') {
        tiles.forEach((t) => t.classList.add('is-in', 'is-done'));
        [this.io, this.nearIO].forEach((o) => o && o.disconnect());
        this.io = this.nearIO = null;
        return;
      }
      if (!this.nearIO) {
        // promote a tile to its own layer well before it scrolls in, so the reveal never has to rasterise mid-fling
        this.nearIO = new IntersectionObserver((entries) => {
          entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add('is-near'); this.nearIO.unobserve(e.target); } });
        }, { rootMargin: '0px 0px 75% 0px' });
        this.io = new IntersectionObserver((entries) => {
          entries.forEach((e) => {
            if (!e.isIntersecting) return;
            const t = e.target;
            t.classList.add('is-in');
            this.io.unobserve(t);
            const done = (ev) => { if (ev.target === t && ev.propertyName === 'transform') { t.classList.add('is-done'); t.removeEventListener('transitionend', done); } };
            t.addEventListener('transitionend', done);
          });
        }, { threshold: 0.05, rootMargin: '0px 0px -4% 0px' });
      }
      tiles.forEach((t) => {
        if (!t.classList.contains('is-near')) this.nearIO.observe(t);
        if (!t.classList.contains('is-in')) this.io.observe(t);
      });
    }

    reset() {
      const E = this.el;
      [E.sticky, E.hero, E.shade, E.fshadow, E.fold, E.cue, E.title, E.wipe, E.wipeIn].concat(E.lines, E.cards).forEach((n) => {
        if (!n) return;
        ['transform', 'opacity', 'filter'].forEach((p) => put(n, p, ''));
      });
      if (E.cue) E.cue.classList.remove('is-off');
      this.setCovered(false);
    }

    // scroll position -> target progress (0..1); also pins the frame by hand if position:sticky is defeated
    measure() {
      const E = this.el, g = this.geo;
      const r = E.stage.getBoundingClientRect();
      const pin = clamp(-r.top, 0, g.D);
      if (!this.pinChecked && r.top < -40 && pin < g.D) {
        this.pinChecked = true;
        this.pinJS = Math.abs(E.sticky.getBoundingClientRect().top) > 3;
      }
      put(E.sticky, 'transform', this.pinJS ? `translate3d(0,${pin}px,0)` : '');
      return pin / g.D;
    }

    // jump straight to the current scroll position (setup, resize, coming back on screen)
    update() {
      if (!this.el.stage || !this.el.fold) return;
      if (!this.mode) { this.reset(); return; }
      this.cur = this.measure();
      this.render(this.cur);
    }

    tick(now) {
      this.raf = 0;
      if (!this.mode || this.away) return;
      const dt = Math.min(64, Math.max(0, now - (this.last || now)));
      this.last = now;
      const target = this.measure();
      this.cur += (target - this.cur) * (1 - Math.exp(-dt / SMOOTH_MS));
      if (Math.abs(target - this.cur) < 0.0005) this.cur = target;
      this.render(this.cur);
      if (this.cur !== target) this.raf = requestAnimationFrame(this.tick);
    }

    render(t) {
      const E = this.el, g = this.geo;
      const T = E.title ? T_TITLE : T_NOTITLE;

      // 1 — the fold slides up over the hero; the film recedes and dims (black overlay, not a filter on the video)
      const s = ease(clamp(t / T.fold));
      const fy = (1 - s) * g.H;
      put(E.fold, 'transform', `translate3d(0,${fy.toFixed(1)}px,0)`);
      put(E.fshadow, 'transform', `translate3d(0,${(fy - 130).toFixed(1)}px,0)`);
      put(E.hero, 'transform', `translate3d(0,${(-12 * s).toFixed(2)}%,0) scale(${(1 + 0.06 * s).toFixed(4)})`);
      put(E.shade, 'opacity', (0.5 * s).toFixed(3));
      if (E.cue) {
        const c = clamp(1 - t * 14);
        E.cue.classList.toggle('is-off', c <= 0);
        put(E.cue, 'opacity', c.toFixed(3));
      }
      this.setCovered(s >= 0.999);

      // 2 — the title is written in: a window slides open (transform only) while the title fades, rises and sharpens
      const h = easeOut(clamp((t - 0.2) / 0.14));
      const wx = ((1 - h) * g.wipe).toFixed(1);
      put(E.title, 'opacity', Math.min(1, h * 1.4).toFixed(3));
      put(E.title, 'filter', h < 1 ? `blur(${((1 - h) * 6).toFixed(2)}px)` : 'none');
      put(E.title, 'transform', `translate3d(0,${((1 - h) * 14).toFixed(2)}px,0)`);
      put(E.wipe, 'transform', `translate3d(${-wx}px,0,0)`);
      put(E.wipeIn, 'transform', `translate3d(${wx}px,0,0)`);

      // 3 — the cards are dealt from alternate sides and land in the fan
      g.cards.forEach((k, i) => {
        const c = E.cards[i];
        const raw = clamp((t - (T.cards + i * T.cardStep)) / T.cardDur);
        const fromLeft = i % 2 === 1;
        const startX = fromLeft ? -k.cw * 0.9 : g.W + k.cw * 0.9;
        const startY = k.ry + g.H * (0.22 - i * 0.035);
        const p = easeOut(raw);
        const x = startX + (k.rx - startX) * p;
        const y = startY + (k.ry - startY) * p - Math.sin(Math.PI * p) * g.H * 0.08;
        const rot = (fromLeft ? -1 : 1) * (34 + i * 6) * (1 - easeOutBack(raw));
        const sc = 1 + 0.16 * (1 - p);
        put(c, 'opacity', raw > 0 ? '1' : '0');
        put(c, 'transform', `translate3d(${(x - k.rx).toFixed(1)}px,${(y - k.ry).toFixed(1)}px,0) rotate(${rot.toFixed(2)}deg) scale(${sc.toFixed(3)})`);
      });

      // 4 — the copy rises in line by line (phone lines are inline: they fade and sharpen in turn)
      E.lines.forEach((ln, k) => {
        const e = easeOut(clamp((t - (T.copy + k * T.lineStep)) / T.lineDur));
        put(ln, 'opacity', e.toFixed(3));
        put(ln, 'transform', `translate3d(0,${((1 - e) * 20).toFixed(2)}px,0)`);
        put(ln, 'filter', e < 1 ? `blur(${((1 - e) * 4).toFixed(2)}px)` : 'none');
      });
    }

    // Film: pause while the fold fully covers it; otherwise keep it playing (the theme can leave autoplay paused).
    setCovered(c) {
      if (c === this.covered) return;
      this.covered = c;
      const v = this.el.video;
      if (!v) return;
      if (c) v.pause(); else this.play();
    }

    play() {
      const v = this.el.video;
      if (!v || this.covered || document.hidden || mqReduce.matches) return;
      const p = v.play();
      if (p && p.catch) p.catch(() => {});
    }

    initVideo() {
      const v = this.el.video;
      if (!v) return;
      v.muted = true; v.playsInline = true;
      if (mqReduce.matches) { v.removeAttribute('autoplay'); v.pause(); return; }
      const kick = () => this.play();
      ['loadeddata', 'canplay', 'pause'].forEach((e) => v.addEventListener(e, kick));
      window.addEventListener('pageshow', kick);
      document.addEventListener('visibilitychange', kick);
      ['touchstart', 'click', 'scroll'].forEach((e) => window.addEventListener(e, kick, { once: true, passive: true, capture: true }));
      kick();
    }
  }

  const init = (scope) => {
    (scope || document).querySelectorAll('[data-k24]').forEach((root) => {
      if (!root.__k24) root.__k24 = new K24(root);
    });
  };

  window.K24Drop = { init };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();

  // Theme editor: re-init a re-rendered section.
  document.addEventListener('shopify:section:load', (e) => init(e.target));
  document.addEventListener('shopify:section:unload', (e) => {
    const root = e.target.querySelector('[data-k24]');
    if (root && root.__k24) root.__k24.destroy();
  });
})();
